import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { ChevronLeft, Save, Loader2, X, FileText, Image as ImageIcon, Sparkles } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { BOOK_FILE_FORMATS, IMAGE_FILE_FORMATS, isValidBookFile, isValidImageFile } from '../../lib/constants'
import { BOOK_GENRES, type BookGenre } from '../../types'
import { SingleLanguageSelector } from '../shared/SingleLanguageSelector'
import { CoverPromptDialog } from './CoverPromptDialog'
import { DescriptionPromptDialog } from './DescriptionPromptDialog'
import { Settings } from 'lucide-react'

export function AdminBookForm() {
  const { bookId } = useParams<{ bookId: string }>()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const isEditMode = !!bookId

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [generatingCover, setGeneratingCover] = useState(false)
  const [generatingDescription, setGeneratingDescription] = useState(false)
  const [showPromptDialog, setShowPromptDialog] = useState(false)
  const [showDescriptionPromptDialog, setShowDescriptionPromptDialog] = useState(false)

  // Form fields
  const [titleOriginal, setTitleOriginal] = useState('')
  const [titleInternational, setTitleInternational] = useState('')
  const [authorOriginal, setAuthorOriginal] = useState('')
  const [authorInternational, setAuthorInternational] = useState('')
  const [originalLanguage, setOriginalLanguage] = useState('')
  const [description, setDescription] = useState('')
  const [genre, setGenre] = useState<BookGenre | ''>('')
  const [bookFormat, setBookFormat] = useState<string | null>(null)
  const [estimatedPages, setEstimatedPages] = useState<number | null>(null)
  const [originalFile, setOriginalFile] = useState<File | null>(null)
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null)
  const [extractedCover, setExtractedCover] = useState<string | null>(null)

  // State to track if metadata has been loaded from file
  const [fileAnalyzed, setFileAnalyzed] = useState(false)
  
  // Drag and drop state
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [isDraggingCover, setIsDraggingCover] = useState(false)

  useEffect(() => {
    if (isEditMode) {
      loadBook()
    }
  }, [bookId])

  const loadBook = async () => {
    if (!bookId) return

    try {
      setLoading(true)
      const data = await apiClient.getAdminBook(bookId)
      const book = data.book

      setTitleOriginal(book.title_original)
      setTitleInternational(book.title_international)
      setAuthorOriginal(book.author_original)
      setAuthorInternational(book.author_international)
      setOriginalLanguage(book.original_language || '')
      setDescription(book.description || '')
      setGenre(book.genre || '')
      setFileAnalyzed(true) // In edit mode, treat as if file was analyzed

      if (book.cover_image_base64) {
        setCoverImagePreview(`data:image/jpeg;base64,${book.cover_image_base64}`)
      }
    } catch (error) {
      console.error('Failed to load book:', error)
      alert('Failed to load book')
    } finally {
      setLoading(false)
    }
  }

  const handleOriginalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setOriginalFile(file)
    setAnalyzing(true)

    try {
      // Analyze file to extract metadata
      const result = await apiClient.analyzeBookFile(file)
      
      if (result.success && result.metadata) {
        const meta = result.metadata
        
        // Prefill fields with extracted data
        if (meta.title_original) setTitleOriginal(meta.title_original)
        if (meta.title_international) setTitleInternational(meta.title_international)
        if (meta.author_original) setAuthorOriginal(meta.author_original)
        if (meta.author_international) setAuthorInternational(meta.author_international)
        if (meta.description) setDescription(meta.description)
        if (meta.detected_language) setOriginalLanguage(meta.detected_language)
        if (meta.format) setBookFormat(meta.format)
        if (meta.estimated_pages) setEstimatedPages(meta.estimated_pages)
        
        // Set extracted cover image
        if (meta.cover_image_base64) {
          const coverDataUrl = `data:image/jpeg;base64,${meta.cover_image_base64}`
          setExtractedCover(coverDataUrl)
          setCoverImagePreview(coverDataUrl)
        }
        
        setFileAnalyzed(true)
      }
    } catch (error) {
      console.error('Failed to analyze file:', error)
      alert('Failed to analyze file. You can still fill in the fields manually.')
      setFileAnalyzed(true) // Allow manual entry even if analysis fails
    } finally {
      setAnalyzing(false)
    }
  }

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingFile(false)
    
    if (analyzing) return

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      // Check file type
      if (isValidBookFile(file)) {
        // Create a synthetic event to reuse existing handler
        const syntheticEvent = {
          target: {
            files: [file]
          }
        } as unknown as React.ChangeEvent<HTMLInputElement>
        await handleOriginalFileChange(syntheticEvent)
      } else {
        alert(`Please upload a valid book file. Supported formats: ${BOOK_FILE_FORMATS.displayString}`)
      }
    }
  }

  const handleFileDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingFile(true)
  }

  const handleFileDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingFile(false)
  }

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCoverImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setCoverImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      setExtractedCover(null) // Clear extracted cover when user uploads their own
    }
  }

  const handleCoverDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingCover(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      // Check if it's an image
      if (isValidImageFile(file)) {
        const syntheticEvent = {
          target: {
            files: [file]
          }
        } as unknown as React.ChangeEvent<HTMLInputElement>
        handleCoverImageChange(syntheticEvent)
      } else {
        alert(`Please upload a valid image file. Supported formats: ${IMAGE_FILE_FORMATS.displayString}`)
      }
    }
  }

  const handleCoverDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingCover(true)
  }

  const handleCoverDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingCover(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!titleOriginal || !titleInternational || !authorOriginal || !authorInternational || !genre) {
      alert('Please fill in all required fields')
      return
    }

    if (!isEditMode && !originalFile) {
      alert('Please upload the original book file')
      return
    }

    try {
      setSaving(true)

      if (isEditMode) {
        // Update existing book
        await apiClient.updateOriginalBook(bookId!, {
          title_original: titleOriginal,
          title_international: titleInternational,
          author_original: authorOriginal,
          author_international: authorInternational,
          original_language: originalLanguage || undefined,
          description: description || undefined,
          genre: genre || undefined,
          cover_image: coverImage || undefined,
        })
        alert('Book updated successfully!')
        navigate(`/admin/books/${bookId}`)
      } else {
        // Create new book
        // If we have an extracted cover but no user-uploaded cover, convert extracted to File
        let coverToSend = coverImage
        if (!coverImage && extractedCover) {
          // Convert base64 data URL to File
          const base64Data = extractedCover.split(',')[1]
          const byteString = atob(base64Data)
          const mimeString = extractedCover.split(',')[0].split(':')[1].split(';')[0]
          const ab = new ArrayBuffer(byteString.length)
          const ia = new Uint8Array(ab)
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i)
          }
          const blob = new Blob([ab], { type: mimeString })
          coverToSend = new File([blob], 'cover.jpg', { type: mimeString })
        }

        const result = await apiClient.createOriginalBook({
          title_original: titleOriginal,
          title_international: titleInternational,
          author_original: authorOriginal,
          author_international: authorInternational,
          description: description || undefined,
          genre: genre || undefined,
          original_language: originalLanguage || undefined,
          format: bookFormat || undefined,
          estimated_pages: estimatedPages || undefined,
          original_file: originalFile!,
          cover_image: coverToSend || undefined,
        })
        alert('Book created successfully!')
        // Navigate to book detail page where translation matrix will be shown
        navigate(`/admin/books/${result.book_id}`)
      }
    } catch (error) {
      console.error('Failed to save book:', error)
      alert(`Failed to ${isEditMode ? 'update' : 'create'} book. Please try again.`)
    } finally {
      setSaving(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleRemoveFile = () => {
    setOriginalFile(null)
    setFileAnalyzed(false)
    setTitleOriginal('')
    setTitleInternational('')
    setAuthorOriginal('')
    setAuthorInternational('')
    setDescription('')
    setBookFormat(null)
    setEstimatedPages(null)
    setCoverImagePreview(null)
    setExtractedCover(null)
  }

  const handleRestoreExtractedCover = () => {
    if (extractedCover) {
      setCoverImagePreview(extractedCover)
      setCoverImage(null)
    }
  }

  const handleGenerateCover = () => {
    if (!isEditMode || !bookId) {
      alert('Please save the book first before generating a cover')
      return
    }
    setShowPromptDialog(true)
  }

  const handleConfirmPrompt = async (promptTemplate: string, options?: { model?: string; useDescription?: boolean }) => {
    setShowPromptDialog(false)
    
    if (!bookId) return

    try {
      setGeneratingCover(true)
      const result = await apiClient.generateBookCover(bookId, promptTemplate, options)
      
      if (result.success && result.cover_base64) {
        // Update preview with the generated cover
        const coverDataUrl = `data:image/jpeg;base64,${result.cover_base64}`
        setCoverImagePreview(coverDataUrl)
        setExtractedCover(null)
        
        // Convert base64 to File object so it can be saved when user clicks "Update Book"
        const base64Data = result.cover_base64
        const byteString = atob(base64Data)
        const ab = new ArrayBuffer(byteString.length)
        const ia = new Uint8Array(ab)
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([ab], { type: 'image/jpeg' })
        const file = new File([blob], 'ai-cover.jpg', { type: 'image/jpeg' })
        setCoverImage(file)
        
        alert('Cover generated successfully! Click "Update Book" to save it.')
      }
    } catch (error: any) {
      console.error('Failed to generate cover:', error)
      alert(error.message || 'Failed to generate cover. Please try again.')
    } finally {
      setGeneratingCover(false)
    }
  }

  const handleGenerateDescription = async () => {
    // Use international names if available, fallback to original
    const displayTitle = titleInternational || titleOriginal || 'Book Title'
    const displayAuthor = authorInternational || authorOriginal || 'Author Name'
    
    // Build default prompt
    const defaultPrompt = `Generate a Book blurb for book "${displayTitle}" by ${displayAuthor} that will be added to my book store. up to 100 words.

Start with the hook: One strong sentence that tells the central conflict, question, or promise. It must make the reader curious. Describe the central tension without spoilers. Match the tone to the genre. Keep paragraphs short (2–4 lines). Use active, simple language. End with a cliffhanger or promise.

Check internet if needed.`

    try {
      setGeneratingDescription(true)
      const result = await apiClient.generateBookDescription(displayTitle, displayAuthor, defaultPrompt)
      
      if (result.success && result.description) {
        setDescription(result.description)
        alert('Description generated successfully! Review it and click "Update Book" to save.')
      }
    } catch (error: any) {
      console.error('Failed to generate description:', error)
      alert(error.message || 'Failed to generate description. Please try again.')
    } finally {
      setGeneratingDescription(false)
    }
  }

  const handleConfirmDescriptionPrompt = async (promptTemplate: string) => {
    setShowDescriptionPromptDialog(false)
    
    // Use international names if available, fallback to original
    const displayTitle = titleInternational || titleOriginal || 'Book Title'
    const displayAuthor = authorInternational || authorOriginal || 'Author Name'
    
    // Replace placeholders in template
    const prompt = promptTemplate
      .replace(/{title}/g, displayTitle)
      .replace(/{author}/g, displayAuthor)

    try {
      setGeneratingDescription(true)
      const result = await apiClient.generateBookDescription(displayTitle, displayAuthor, prompt)
      
      if (result.success && result.description) {
        setDescription(result.description)
        alert('Description generated successfully! Review it and click "Update Book" to save.')
      }
    } catch (error: any) {
      console.error('Failed to generate description:', error)
      alert(error.message || 'Failed to generate description. Please try again.')
    } finally {
      setGeneratingDescription(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">Loading book...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/management')}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Books
          </button>

          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-primary rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {isEditMode ? 'Edit Book' : 'Add New Book'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isEditMode ? 'Update book information' : 'Upload a book file to automatically extract information'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Book Information */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Book Information</CardTitle>
                  <CardDescription>
                    {!isEditMode && 'Step 1: Upload the original book file to extract metadata'}
                    {isEditMode && 'Update book metadata'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Original File Upload - Step 1 */}
                  {!isEditMode && (
                    <div className="space-y-2">
                      <Label htmlFor="original-file">
                        Original Book File <span className="text-red-500">*</span>
                      </Label>
                      {!originalFile ? (
                        <div 
                          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                            isDraggingFile 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary'
                          }`}
                          onDrop={handleFileDrop}
                          onDragOver={handleFileDragOver}
                          onDragLeave={handleFileDragLeave}
                        >
                          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                          <input
                            id="original-file"
                            type="file"
                            accept={BOOK_FILE_FORMATS.acceptString}
                            onChange={handleOriginalFileChange}
                            className="hidden"
                            disabled={analyzing}
                          />
                          <label
                            htmlFor="original-file"
                            className="cursor-pointer"
                          >
                            <span className="text-primary hover:underline font-medium">
                              {isDraggingFile ? 'Drop file here' : 'Drag and drop or click to upload book file'}
                            </span>
                            <p className="text-xs text-muted-foreground mt-2">
                              Supported: {BOOK_FILE_FORMATS.displayString}
                            </p>
                          </label>
                          {analyzing && (
                            <div className="mt-4">
                              <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                              <p className="text-sm text-muted-foreground mt-2">
                                Analyzing file and extracting metadata...
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="border border-border rounded-lg p-4 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-primary" />
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {originalFile.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {(originalFile.size / 1024).toFixed(2)} KB
                                  {estimatedPages && (
                                    <span className="ml-2">• ~{estimatedPages} pages</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleRemoveFile}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Divider */}
                  {!isEditMode && fileAnalyzed && (
                    <div className="border-t border-border pt-6">
                      <p className="text-sm text-muted-foreground mb-4">
                        Step 2: Review and edit the extracted information
                      </p>
                    </div>
                  )}

                  {/* Form Fields - Disabled until file is uploaded */}
                  <div className={`space-y-6 ${!fileAnalyzed && !isEditMode ? 'opacity-50 pointer-events-none' : ''}`}>
                    {/* Title Original */}
                    <div className="space-y-2">
                      <Label htmlFor="title-original">
                        Original Title <span className="text-red-500">*</span>
                      </Label>
                      <input
                        id="title-original"
                        type="text"
                        value={titleOriginal}
                        onChange={(e) => setTitleOriginal(e.target.value)}
                        placeholder="e.g., Три сестры"
                        required
                        disabled={!fileAnalyzed && !isEditMode}
                        className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Title International */}
                    <div className="space-y-2">
                      <Label htmlFor="title-international">
                        International Title (English) <span className="text-red-500">*</span>
                      </Label>
                      <input
                        id="title-international"
                        type="text"
                        value={titleInternational}
                        onChange={(e) => setTitleInternational(e.target.value)}
                        placeholder="e.g., Three Sisters"
                        required
                        disabled={!fileAnalyzed && !isEditMode}
                        className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Author Original */}
                    <div className="space-y-2">
                      <Label htmlFor="author-original">
                        Original Author <span className="text-red-500">*</span>
                      </Label>
                      <input
                        id="author-original"
                        type="text"
                        value={authorOriginal}
                        onChange={(e) => setAuthorOriginal(e.target.value)}
                        placeholder="e.g., Антон Чехов"
                        required
                        disabled={!fileAnalyzed && !isEditMode}
                        className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Author International */}
                    <div className="space-y-2">
                      <Label htmlFor="author-international">
                        International Author (English) <span className="text-red-500">*</span>
                      </Label>
                      <input
                        id="author-international"
                        type="text"
                        value={authorInternational}
                        onChange={(e) => setAuthorInternational(e.target.value)}
                        placeholder="e.g., Anton Chekhov"
                        required
                        disabled={!fileAnalyzed && !isEditMode}
                        className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Original Language */}
                    <div className="space-y-2">
                      <Label htmlFor="original-language">
                        Original Language
                      </Label>
                      <SingleLanguageSelector
                        value={originalLanguage}
                        onChange={setOriginalLanguage}
                        disabled={!fileAnalyzed && !isEditMode}
                      />
                      <p className="text-xs text-muted-foreground">
                        Language is auto-detected during upload. You can change it here if needed.
                      </p>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="description">Description</Label>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleGenerateDescription}
                            disabled={generatingDescription || (!fileAnalyzed && !isEditMode)}
                            className="h-7 text-xs"
                          >
                            {generatingDescription ? (
                              <>
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="mr-1 h-3 w-3" />
                                Generate AI Description
                              </>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDescriptionPromptDialog(true)}
                            disabled={generatingDescription || (!fileAnalyzed && !isEditMode)}
                            className="h-7 w-7 p-0"
                            title="Choose Description Prompt Template"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter a brief description of the book..."
                        rows={6}
                        disabled={!fileAnalyzed && !isEditMode}
                        className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Genre */}
                    <div className="space-y-2">
                      <Label htmlFor="genre">
                        Genre <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id="genre"
                        value={genre}
                        onChange={(e) => setGenre(e.target.value as BookGenre)}
                        required
                        disabled={!fileAnalyzed && !isEditMode}
                        className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">Select a genre</option>
                        {BOOK_GENRES.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/management')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving || (!fileAnalyzed && !isEditMode)}
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {isEditMode ? 'Update Book' : 'Create Book'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Right Column - Cover Image */}
            <div className="lg:col-span-1">
              <Card className="sticky top-6">
                <CardHeader>
                  <CardTitle>Cover Image</CardTitle>
                  <CardDescription>
                    {extractedCover && !coverImage 
                      ? 'Extracted from book file'
                      : 'Upload a custom cover image'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Cover Preview */}
                  {coverImagePreview && (
                    <div className="relative aspect-[2/3] border border-border rounded-lg overflow-hidden bg-muted">
                      <img
                        src={coverImagePreview}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCoverImage(null)
                          setCoverImagePreview(null)
                          setExtractedCover(null)
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {/* Upload/Replace Button */}
                  <div className="space-y-2">
                    <div 
                      className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                        isDraggingCover
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary'
                      } ${!coverImagePreview ? 'aspect-[2/3] flex flex-col items-center justify-center' : ''}`}
                      onDrop={handleCoverDrop}
                      onDragOver={handleCoverDragOver}
                      onDragLeave={handleCoverDragLeave}
                    >
                      <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <input
                        id="cover-image"
                        type="file"
                        accept={IMAGE_FILE_FORMATS.acceptString}
                        onChange={handleCoverImageChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="cover-image"
                        className="cursor-pointer"
                      >
                        <span className="text-sm text-primary hover:underline">
                          {isDraggingCover ? 'Drop image here' : (coverImagePreview ? 'Drag and drop or click to replace' : 'Drag and drop or click to upload')}
                        </span>
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        {IMAGE_FILE_FORMATS.displayString}
                      </p>
                    </div>

                    {/* Restore extracted cover button */}
                    {extractedCover && coverImage && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRestoreExtractedCover}
                        className="w-full"
                      >
                        Restore extracted cover
                      </Button>
                    )}

                    {/* Generate AI Cover button - only in edit mode */}
                    {isEditMode && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateCover}
                        disabled={generatingCover}
                        className="w-full"
                      >
                        {generatingCover ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-4 w-4" />
                            Generate AI Cover
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>

      {/* Cover Prompt Dialog */}
      {showPromptDialog && (
        <CoverPromptDialog
          open={showPromptDialog}
          onClose={() => setShowPromptDialog(false)}
          onConfirm={handleConfirmPrompt}
          title={titleInternational || titleOriginal || 'Book Title'}
          author={authorInternational || authorOriginal || 'Author Name'}
          description={description}
        />
      )}

      {/* Description Prompt Dialog */}
      {showDescriptionPromptDialog && (
        <DescriptionPromptDialog
          open={showDescriptionPromptDialog}
          onClose={() => setShowDescriptionPromptDialog(false)}
          onConfirm={handleConfirmDescriptionPrompt}
          title={titleInternational || titleOriginal || 'Book Title'}
          author={authorInternational || authorOriginal || 'Author Name'}
        />
      )}
    </div>
  )
}
